import React, { useState, useEffect } from "react";

// Components
import { SectionCard, Row, SearchWrapper } from "./BaseProducts.styles";
import { Wrapper } from "../ProductInfo/ProductInfo.styles";
import { HeaderText } from "../Header/Header.styles";
import ProductsTable from "./ProductsTable";
import { Input, notification } from "antd";
import SearchResults from "./SearchResults";

// Hooks
import { useParams } from "react-router";
import useProductCreateFlow from "../../../../hooks/reduxContainer/fulfillment/productCreateFlow";
import { useProducts } from "../../../../hooks/api/fulfillment/products";

// Interfaces
import { debounce } from "lodash-es";
import { Product } from "../../../../interfaces/products";

const { Search } = Input;

const BaseProducts = () => {
  const [showResults, setShowResults] = useState(true);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const params: { organization_id: string } = useParams();
  const { isEditing, product, dispatchSetBaseProducts, dispatchSetRequiredParts } = useProductCreateFlow();
  const { organization_id: organizationID } = params;
  const { products, fetchProducts } = useProducts();
  const { error, loading } = products;
  const { base_products: baseProducts = [], required_parts: requiredParts } = product;

  useEffect(() => {
    if (error)
      notification.error({
        message: "Error",
        description: error,
      });
  }, [error]);

  const searchProducts = debounce(async (searchQuery: string) => {
    const products = await fetchProducts({
      product_type: "base",
      q: searchQuery,
      organization_id: organizationID,
    });

    if (products) {
      const filteredProducts = products.filter(
        (i) => !baseProducts.length || !baseProducts.find((baseProduct) => baseProduct.id === i.id)
      );
      setSearchResults(filteredProducts);
    }
    return products;
  }, 500);

  const handleOnSelect = (baseProduct: Product) => {
    const alreadySelected =
      baseProducts && baseProducts.length
        ? baseProducts.filter((component) => component.id === baseProduct.id).length > 0
        : false;

    const isValidProductObject = baseProduct.id;

    if (!alreadySelected && isValidProductObject) {
      const newBaseProducts = baseProducts
        ? ([...baseProducts, baseProduct] as Product["base_products"])
        : ([baseProduct] as Product["base_products"]);
      dispatchSetBaseProducts(newBaseProducts);

      const newRequiredParts = {
        ...requiredParts,
        [baseProduct.id]: 1,
      };
      dispatchSetRequiredParts(newRequiredParts);
    }
    setShowResults(false);
  };

  const handleChangeQuantity = (baseProductID: string, quantity: number) => {
    const newRequiredParts = requiredParts
      ? { ...requiredParts, [baseProductID]: quantity }
      : { [baseProductID]: quantity };
    dispatchSetRequiredParts(newRequiredParts);
  };

  const handleRemoveComponent = (baseProductID: string) => {
    const newRequiredParts = { ...requiredParts, [baseProductID]: 0 };
    dispatchSetRequiredParts(newRequiredParts);
    const newBaseProducts = baseProducts.filter((baseProduct) => baseProduct.id !== baseProductID);
    dispatchSetBaseProducts(newBaseProducts);
  };

  const setShowResultsValue = (value: boolean) => {
    setShowResults(value);
  };

  return (
    <Wrapper>
      <HeaderText margin="0 0 16px">{`${isEditing ? "SELECT " : ""}BASE PRODUCTS`}</HeaderText>
      <SectionCard isEditing={isEditing}>
        {isEditing ? (
          <Row>
            <SearchWrapper id="sub-component-search" width={800}>
              <Search
                enterButton
                placeholder="Search for Products"
                onSearch={(value) => searchProducts(value)}
                onChange={(e) => searchProducts(e.target.value)}
                loading={loading}
                id="product-search"
              />
              <SearchResults
                searchResults={searchResults}
                showResults={showResults}
                setShowResultsValue={(value) => setShowResultsValue(value)}
                handleOnSelect={handleOnSelect}
                inputID="product-search"
                width={
                  document.getElementById("sub-component-search")
                    ? document.getElementById("sub-component-search")?.offsetWidth
                    : 560
                }
                maxHeight={262}
              />
            </SearchWrapper>
          </Row>
        ) : null}
        <ProductsTable
          requiredParts={requiredParts}
          selectedProducts={baseProducts}
          handleChangeQuantity={handleChangeQuantity}
          handleRemoveComponent={handleRemoveComponent}
          isEditing={isEditing}
        />
      </SectionCard>
    </Wrapper>
  );
};

export default BaseProducts;
